import React, { useState, useEffect } from 'react';
// Stripe Elements no longer required since we use Checkout for Add Funds
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import FeaturedItems from './components/FeaturedItems';
import HowItWorks from './components/HowItWorks';
import BrowsePage from './components/BrowsePage';
import ContactPage from './components/ContactPage';
import OwnerClosetPage from './components/OwnerClosetPage';
import AboutSection from './components/AboutSection';
import FAQPage from './components/FAQPage';
import LegalPage from './components/LegalPage';
import Newsletter from './components/Newsletter';
import Footer from './components/Footer';
import TradeModal from './components/TradeModal';
import LoginModal from './components/LoginModal';
import DashboardModal from './components/DashboardModal';
import AdminModal from './components/AdminModal';
import PasswordResetModal from './components/PasswordResetModal';
import { authAPI, paymentsAPI, balanceAPI } from './services/api';

// Component to handle dynamic page titles
const PageTitleHandler = () => {
  const location = useLocation();
  
  useEffect(() => {
                const pathToTitle = {
                  '/': 'Ooowap - Trade Luxury Purses & Handbags',
                  '/browse': 'Browse Purses - Ooowap',
                  '/contact': 'Contact Us - Ooowap',
                  '/faq': 'FAQ - Ooowap',
                  '/legal/terms-of-service': 'Terms of Service - Ooowap',
                  '/legal/privacy-policy': 'Privacy Policy - Ooowap',
                  '/legal/user-agreement': 'User Agreement - Ooowap',
                  '/signup': 'Sign Up - Ooowap',
                  '/login': 'Login - Ooowap'
                };
    
    const title = pathToTitle[location.pathname] || 'Ooowap - Trade Luxury Purses & Handbags';
    document.title = title;
  }, [location]);
  
  return null;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetToken, setPasswordResetToken] = useState(null);
  const [selectedHat, setSelectedHat] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardActiveTab, setDashboardActiveTab] = useState('overview');

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const user = authAPI.getCurrentUser();
        console.log('Stored user from localStorage:', user); // Debug log
        if (user) {
          setIsLoggedIn(true);
          // Handle both possible property names for admin status
          const isAdminUser = user.isAdmin || user.is_admin || false;
          console.log('Admin status:', isAdminUser); // Debug log
          setIsAdmin(isAdminUser);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Stripe Elements not needed for Checkout

  // Handle email verification via /verify-email?token=...
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.pathname === '/verify-email') {
        const token = url.searchParams.get('token');
        if (token) {
          (async () => {
            try {
              await authAPI.verifyEmail(token);
              alert('Email verified! You can now log in.');
            } catch (e) {
              alert('Verification failed: ' + (e.message || 'Invalid or expired link'));
            } finally {
              // Clean URL back to home
              window.history.replaceState({}, document.title, '/');
            }
          })();
        }
      }
      // Handle password reset via /reset-password?token=...
      if (url.pathname === '/reset-password') {
        const token = url.searchParams.get('token');
        if (token) {
          // Show password reset modal
          setShowPasswordResetModal(true);
          setPasswordResetToken(token);
          // Clean URL but keep token in state
          window.history.replaceState({}, document.title, '/reset-password');
        }
      }
      // Handle add funds return from Stripe Checkout
      const addFunds = url.searchParams.get('add_funds');
      if (addFunds === 'success') {
        try { window.localStorage.setItem('add_funds_success', '1'); } catch {}
        setDashboardActiveTab('balance');
        setShowDashboardModal(true);
        // Attempt server-side verification and refresh
        const sid = url.searchParams.get('session_id');
        if (sid) {
          (async () => {
            try { await paymentsAPI.verifySession(sid); } catch {}
            try { await balanceAPI.getBalance(); } catch {}
          })();
        }
        // Clean URL
        window.history.replaceState({}, document.title, '/');
      } else if (addFunds === 'cancel') {
        try { window.localStorage.setItem('add_funds_cancel', '1'); } catch {}
        setDashboardActiveTab('balance');
        setShowDashboardModal(true);
        window.history.replaceState({}, document.title, '/');
      }
    } catch {}
  }, []);

  const handleTradeRequest = (hat) => {
    if (!isLoggedIn) {
      // If not logged in, show login modal
      setShowLoginModal(true);
    } else {
      // If logged in, proceed with trade request
      setSelectedHat(hat);
      setShowTradeModal(true);
    }
  };

  const handleStartTrading = () => {
    // Navigate to browse page to start trading
    window.location.href = '/browse';
  };

  const handleUploadHat = () => {
    if (!isLoggedIn) {
      // If not logged in, show login modal
      setShowLoginModal(true);
    } else {
      // If logged in, open dashboard modal with upload tab active
      setDashboardActiveTab('upload-hat');
      setShowDashboardModal(true);
    }
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      console.log('Login response:', response); // Debug log
      console.log('User object:', response.user); // Debug log
      console.log('is_admin value:', response.user.is_admin); // Debug log
      
      setIsLoggedIn(true);
      setIsAdmin(response.user.is_admin);
      setCurrentUser(response.user);
      setShowLoginModal(false);
      
      // Show success message
      alert('Login successful!');
    } catch (error) {
      console.error('Login error:', error);
      alert(`Login failed: ${error.message}`);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    setIsLoggedIn(false);
    setIsAdmin(false);
    setCurrentUser(null);
    setShowDashboardModal(false);
    setShowAdminModal(false);
    
    // Show logout message
    alert('Logged out successfully!');
  };

  const handleLoginClick = () => {
    if (isLoggedIn) {
      if (isAdmin) {
        setShowAdminModal(true);
      } else {
        setShowDashboardModal(true);
      }
    } else {
      setShowLoginModal(true);
    }
  };

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '1.2rem',
          color: '#d4af37'
        }}>
          Loading Purse Trading Platform...
        </div>
      </div>
    );
  }

  // Home page component
  const handleGetStarted = () => {
    window.location.href = '/browse';
  };

  const handleJoinCommunity = () => {
    window.open('https://facebook.com/ooowapdotcom', '_blank');
  };

  const HomePage = () => (
    <>
      <Hero onStartTrading={handleStartTrading} onUploadHat={handleUploadHat} />
      <Features />
      <FeaturedItems onTradeRequest={handleTradeRequest} />
      <HowItWorks onGetStarted={handleGetStarted} />
      <div className="about-rotating-container two-col">
        <AboutSection onJoinCommunity={handleJoinCommunity} />
        <div className="rotating-images">
          <div className="image-container" style={{maxWidth: '360px', margin: '0 auto'}}>
            <div style={{position:'relative', width:'100%', height:'280px', overflow:'hidden', borderRadius:8, background:'#000'}}>
              <iframe
                style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                src="https://www.youtube.com/embed/fs7K69fvYO0"
                title="Ooowap Purse Trading PlatForm"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>
      <Newsletter />
    </>
  );

  return (
    <Router>
      <div className="App">
        <PageTitleHandler />
        <Navbar 
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onLoginClick={handleLoginClick}
          currentUser={currentUser}
        />
        
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/browse" element={<BrowsePage onTradeRequest={handleTradeRequest} />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/legal/:type" element={<LegalPage />} />
          <Route path="/closet/:ownerId" element={<OwnerClosetPage onTradeRequest={handleTradeRequest} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        <Footer
          onBrowseHats={() => { window.location.href = '/browse'; }}
          onUploadHat={handleUploadHat}
          onMyTrades={() => {
            if (!isLoggedIn) { setShowLoginModal(true); } else { setDashboardActiveTab('trades'); setShowDashboardModal(true); }
          }}
          onHowItWorks={() => { const el = document.querySelector('.how-it-works'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } else { window.location.href = '/#how-it-works'; } }}
          onSafetyGuidelines={null}
          onFAQ={null}
        />

        {/* Modals */}
        <TradeModal 
          isOpen={showTradeModal}
          onClose={() => setShowTradeModal(false)}
          selectedHat={selectedHat}
          isLoggedIn={isLoggedIn}
        />
        
        <LoginModal 
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLogin={handleLogin}
        />
        
        <DashboardModal 
          isOpen={showDashboardModal}
          onClose={() => setShowDashboardModal(false)}
          onLogout={handleLogout}
          currentUser={currentUser}
          initialActiveTab={dashboardActiveTab}
        />
        
        <AdminModal 
          isOpen={showAdminModal}
          onClose={() => setShowAdminModal(false)}
          onLogout={handleLogout}
          currentUser={currentUser}
        />
        
        <PasswordResetModal 
          isOpen={showPasswordResetModal}
          onClose={() => {
            setShowPasswordResetModal(false);
            setPasswordResetToken(null);
            window.history.replaceState({}, document.title, '/');
          }}
          token={passwordResetToken}
        />
      </div>
    </Router>
  );
}

export default App;
